import {
	Combobox,
	ComboboxList,
	type ComboboxListProps,
	type ComboboxProps,
	Role,
	Select,
	SelectArrow,
	SelectItem,
	type SelectItemProps,
	SelectList,
	type SelectListProps,
	SelectPopover,
	type SelectPopoverProps,
	useSelectContext,
	useStoreState
} from '@ariakit/react'
import { forwardRef, type ReactNode } from 'react'
import { Virtuoso, type VirtuosoProps } from 'react-virtuoso'
import { InputText } from '@/components/commons/input-text'
import type { ButtonProps } from '@/components/ui/button-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ChevronDownIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { cn, raise } from '@/helpers/utils'

export const DESKTOP_TABLE_SCROLLBAR_WIDTH = '4px'

export const SelectPopoverComponent = ({
	className,
	...props
}: SelectPopoverProps) => (
	<SelectPopover
		className={cn(
			'z-(--z-dropdown) grid max-h-[328px] max-w-(--popover-available-width) grid-rows-[minmax(0,1fr)] overflow-y-hidden rounded-lg border border-neutral-200 bg-neutral-white p-1 px-1.5 shadow-lg',
			className
		)}
		gutter={4}
		sameWidth
		{...props}
	/>
)
export { SelectPopoverComponent as SelectPopover }

export const SelectComboboxPopover = ({
	className,
	...props
}: SelectPopoverProps) => (
	<SelectPopover
		className={cn(
			'z-(--z-dropdown) grid max-h-[328px] max-w-[var(--popover-available-width)] grid-rows-[auto_minmax(0,1fr)] gap-1 overflow-y-hidden rounded-lg border border-neutral-200 bg-neutral-white p-1 px-1.5 shadow-lg',
			className
		)}
		gutter={4}
		sameWidth
		{...props}
	/>
)

export const SelectListComponent = (props: SelectListProps) => (
	<ScrollArea overflowX='hidden' scrollbar={<Scrollbar />}>
		<SelectList className='overflow-y-auto' {...props} />
	</ScrollArea>
)
export { SelectListComponent as SelectList }

export const SelectComboboxList = (props: ComboboxListProps) => (
	<ScrollArea overflowX='hidden' scrollbar={<Scrollbar />}>
		<ComboboxList className='overflow-y-auto' {...props} />
	</ScrollArea>
)

export const SelectItemComponent = ({
	className,
	...props
}: SelectItemProps) => (
	<SelectItem
		className={cn(
			'my-px cursor-pointer overflow-x-hidden truncate rounded-md px-2 py-2.5 text-neutral-900 text-sm transition-all duration-150',
			'aria-disabled:text-neutral-400',
			'aria-selected:bg-neutral-100',
			'data-[active-item=true]:not-aria-selected:bg-neutral-50',
			className
		)}
		{...props}
	/>
)
export { SelectItemComponent as SelectItem }

export const SelectComboboxInput = (props: ComboboxProps) => (
	<Combobox
		autoSelect
		render={<InputText inputSize='sm' type='text' />}
		{...props}
	/>
)

interface SelectTriggerProps extends Omit<ButtonProps, 'children'> {
	placeholder?: ReactNode
	displayValue: (value: string | readonly string[]) => ReactNode
}

export const SelectTrigger = forwardRef<HTMLSelectElement, SelectTriggerProps>(
	({ placeholder, displayValue, className, ...rest }, ref) => {
		const selectContext =
			useSelectContext() ??
			raise('SelectTrigger must be in a SelectProvider')
		const storeState = useStoreState(selectContext)
		const value = storeState.value
		const isOpen = storeState.open
		const children = value.length >= 1 ? displayValue(value) : undefined

		return (
			<Select
				render={
					<Role.select
						ref={ref}
						render={
							<ButtonLegacy
								className={cn(
									'justify-between border border-neutral-300 bg-neutral-white hover:not-disabled:bg-neutral-50',
									!children && 'font-normal text-neutral-400',
									'hover:not-disabled:shadow-[0_0_0_4px_var(--color-neutral-100)] hover:not-disabled:outline hover:not-disabled:outline-1 hover:not-disabled:outline-neutral-300',
									'focus:shadow-[0_0_0_4px_var(--color-neutral-100)]',
									'aria-expanded:shadow-[0_0_0_4px_var(--color-neutral-100)] aria-expanded:outline aria-expanded:outline-1 aria-expanded:outline-neutral-300',
									'disabled:bg-neutral-100',
									className
								)}
								type='button'
								variant='secondary'
								{...rest}
							>
								{children || placeholder}
								<SelectArrow
									className={cn(
										'ml-auto fill-neutral-500 transition-transform duration-150',
										isOpen && 'rotate-180'
									)}
									render={<ChevronDownIcon />}
								/>
							</ButtonLegacy>
						}
					/>
				}
			/>
		)
	}
)
SelectTrigger.displayName = 'SelectTrigger'

export const SelectVirtualList = <T,>(props: VirtuosoProps<T, unknown>) => (
	<Virtuoso
		className='!h-[200px] [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-neutral-50'
		{...props}
	/>
)
